import React, { useContext, useEffect } from 'react';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection.ts';
import { HOME, WIKI, SPACE_DIRECTORY } from '@confluence/named-routes/entry-points/routes.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { LazyHome, View, SUPPORTED_ROUTES } from '@confluence/home/entry-points/LazyHome.ts';

export const GoToHome = (): React.JSX.Element => {
	const { isLicensed, accessStatus } = useSessionData();
	const { match, location } = useContext(RoutesContext);
	const { view } = match?.params || {};

	// If /wiki was SSR'd, redirect to /wiki/home after the SPA is loaded
	useWikiRedirectSSR();

	// Redirect to space directory if the user is either anonymous or unlicensed
	if (
		!isLicensed ||
		accessStatus === AccessStatus.ANONYMOUS_ACCESS ||
		accessStatus === AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS
	) {
		return <Redirection name={SPACE_DIRECTORY.name} />;
	}

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!window.__SSR_RENDERED__ && !process.env.REACT_SSR) {
		// Redirect to /wiki/home if one of the following is true:
		//   1. the route param :view is unsupported
		//   2. the recentTab query param is present
		const homeBasePath = new RegExp(`^${CONTEXT_PATH}/home/?$`, 'g');
		const isBaseHomePath = Boolean(location?.pathname.match(homeBasePath));
		const isHomeRoute = SUPPORTED_ROUTES.includes(view as View);
		const { recentTab } = match?.query || {};

		const shouldRedirect = recentTab || (!isBaseHomePath && !isHomeRoute);

		if (shouldRedirect) {
			return <Redirection name={HOME.name} params={getRedirectionParams(match)} />;
		}
	}
	return <LazyHome />;
};

const useWikiRedirectSSR = () => {
	const { replace, match } = useContext(RoutesContext);
	useEffect(() => {
		if (!window.__SSR_RENDERED__ || match?.name !== WIKI.name) {
			return;
		}

		// Ensure that top nav is loaded before transitioning to /wiki/home. This prevents nav flicker.
		// Preloading should have already been initiated before rendering this component.
		// We just "preload" this here so that we can chain to the promise.
		replace(
			HOME.toUrl(undefined, {
				query: match.query,
			}),
		);
	}, [replace, match]);
};

const getRedirectionParams = (match: any) => {
	const { query } = match;
	const { recentTab } = query;

	if (recentTab !== undefined) {
		delete query.recentTab;
		return {
			query,
			view: home1_3RecentTabToHome1_5Route[recentTab],
		};
	}

	return { query };
};

const home1_3RecentTabToHome1_5Route = [View.RECENT, View.RECENT, View.DRAFTS, View.STARRED];
